import React, { FC, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import AdyenCheckout from '@adyen/adyen-web';
import toast from 'react-hot-toast';
import '@adyen/adyen-web/dist/adyen.css';
import { Cart } from 'shared/types/cart/Cart';
import {
  ApplePayPaymentRequestPayload,
  LineItem,
} from 'components/commercetools-ui/organisms/checkout/provider/payment/types';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import aggregatedQuantity from 'helpers/utils/aggregatedQuantity';
import { Guid } from 'helpers/utils/guid';
import { sdk } from 'sdk';
import { useAdyen, useCart, useStock } from 'frontastic';
import {
  applepayLineItems,
  applePayNewTotal,
  validateCartForRestrictedItems,
  validateShippingMethod,
  validateAddress,
} from '../../helper/apply-pay-helper';
import useGivex from 'frontastic/hooks/useGivex';
import { PaymentMethod } from 'components/commercetools-ui/organisms/checkout/provider/payment/types';

interface Props {
  isLoading: boolean | undefined;
}

const ApplePayExpressButton: FC<Props> = ({ isLoading }) => {
  const adyen = useAdyen();
  const { getProductStock } = useStock();
  const {
    transaction,
    data,
    setDeliveryOption,
    updateCart,
    setShippingMethod,
    getAvailableShippingMethods,
    getProjectSettings,
    cartRecalculate,
    productSKUs,
    freezeCart,
    removeInvalidDiscountCodes,
  } = useCart();
  const { getCountry, locale } = useLocale();
  const router = useRouter();
  const { formatMessage: formatCheckoutMessage } = useFormat({ name: 'checkout' });
  const { preauthGiftcards, cancelPreauthGiftcards } = useGivex();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[] | null>(null);

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      const country = getCountry();
      // const isGBP = data?.sum?.currencyCode === 'GBP';

      try {
        const methods = await adyen?.getPaymentMethods(country, locale, data?.sum?.currencyCode === 'GBP');
        setPaymentMethods(methods);
      } catch (error) {
        console.error('Failed to fetch payment methods:', error);
      }
    };

    fetchPaymentMethods();
  }, []);

  useEffect(() => {
    const initAdyenCheckout = async () => {
      //const paymentMethods = await adyen.getPaymentMethods(getCountry(), locale, data?.sum?.currencyCode === 'GBP');
      if (!paymentMethods) return;
      const applePayMethod = paymentMethods.find((method) => method.type === 'applepay');
      const merchantId = applePayMethod?.configuration?.merchantId ?? '';
      const merchantName = applePayMethod?.configuration?.merchantName ?? '';
      let applepayToken = '';

      const configuration = {
        locale: locale,
        environment: process.env.NEXT_PUBLIC_ADYEN_ENV,
        clientKey: process.env.NEXT_PUBLIC_ADYEN_CLIENT_KEY,
      };

      const checkout = await AdyenCheckout(configuration);

      const lineItems = [
        {
          label: formatCheckoutMessage({ id: 'checkout.applepay.subtotal', defaultMessage: 'Merchandise' }),
          amount: CurrencyHelpers.formatMoney(transaction.subtotal).toString(),
        },
        {
          label: data?.shippingInfo?.name,
          amount: CurrencyHelpers.formatMoney(transaction.shipping).toString(),
        },
        {
          label: formatCheckoutMessage({ id: 'checkout.applepay.tax', defaultMessage: 'Tax' }),
          amount: CurrencyHelpers.formatMoney(transaction.tax).toString(),
        },
        {
          label: formatCheckoutMessage({ id: 'checkout.applepay.discount', defaultMessage: 'Discount' }),
          amount: CurrencyHelpers.formatMoney(transaction.discount).toString(),
        },
      ];

      const applePayConfig = {
        buttonColor: 'white',
        buttonType: 'check-out',

        configuration: {
          merchantName: merchantName,
          merchantId: merchantId,
        },
        amount: {
          value: transaction?.total?.centAmount,
          currency: transaction?.total?.currencyCode,
        },
        requiredBillingContactFields: ['postalAddress', 'name', 'email', 'phone'],
        requiredShippingContactFields: ['postalAddress', 'name', 'email', 'phone'],
        lineItems: lineItems,

        onShippingContactSelected: async (
          resolve: (arg0: {
            newTotal: ApplePayJS.ApplePayLineItem;
            newLineItems: ApplePayJS.ApplePayLineItem[];
            newShippingMethods: ApplePayJS.ApplePayShippingMethod[];
          }) => void,
          reject: any,
          event: {
            shippingContact: ApplePayJS.ApplePayPaymentContact;
          },
        ) => {
          const initialAddressData = {
            firstName: event?.shippingContact.givenName,
            lastName: event?.shippingContact.familyName,
            email: event?.shippingContact.emailAddress,
            phone: event?.shippingContact.phoneNumber,
            postalCode: event?.shippingContact.postalCode,
            city: event?.shippingContact.locality,
            county: event?.shippingContact.administrativeArea,
            country: event?.shippingContact.countryCode,
          };
          const checkoutData = {
            shipping: initialAddressData,
          };

          const newTotal: ApplePayJS.ApplePayLineItem = applePayNewTotal(
            await cartRecalculate(),
            locale,
            formatCheckoutMessage({
              id: 'checkout.applepay.label',
              defaultMessage: 'American Golf (Trading) Limited',
            }),
          );

          if (typeof getProjectSettings === 'function') {
            const projectSettings = await getProjectSettings();
            const isDeliveryCountryValid = projectSettings.countries?.find(
              (country) => country == initialAddressData.country,
            );
            if (!isDeliveryCountryValid) {
              const appleErr: never[] = [];

              const applePayShippingContactUpdate = {
                newTotal: newTotal,
                errors: [
                  ...appleErr,
                  new ApplePayError(
                    'shippingContactInvalid',
                    'countryCode',
                    "Unfortunately, your cart can't be fulfilled to your chosen location . Please change your delivery address to continue.",
                  ),
                ],
              };
              reject(applePayShippingContactUpdate);
            }
          }

          setDeliveryOption('HomeDelivery');
          const res = await updateCart(checkoutData);

          const isItemRestricted = validateCartForRestrictedItems(res, initialAddressData);

          if (isItemRestricted) {
            const appleErr: never[] = [];

            const applePayShippingContactUpdate = {
              newTotal: newTotal,
              errors: [
                ...appleErr,
                new ApplePayError(
                  'shippingContactInvalid',
                  'countryCode',
                  "Cart has items which can't be fulfilled, remove items to continue",
                ),
              ],
            };
            reject(applePayShippingContactUpdate);
          }

          const shippingMethods = await getAvailableShippingMethods();

          if (shippingMethods.length == 0) {
            const appleErr: never[] = [];

            const applePayShippingContactUpdate = {
              newTotal: newTotal,
              errors: [
                ...appleErr,
                new ApplePayError(
                  'shippingContactInvalid',
                  'countryCode',
                  "Cart has items which can't be fulfilled, remove items to continue",
                ),
              ],
            };
            reject(applePayShippingContactUpdate);
          }

          const newLineItems: ApplePayJS.ApplePayLineItem[] = applepayLineItems(transaction, res, locale);

          const excludedShippingMethods = process.env.NEXT_PUBLIC_APPLEPAY_EXCLUDED_SHIPPING_METHOD?.split(',').map(
            (str) => str.trim(),
          );
          const newShippingMethods: ApplePayJS.ApplePayShippingMethod[] = [];
          shippingMethods?.forEach((shippingMethod) => {
            if (shippingMethod.key && !excludedShippingMethods?.includes(shippingMethod.key)) {
              const newShippingMethod: ApplePayJS.ApplePayShippingMethod = {
                label: shippingMethod.name !== undefined ? shippingMethod.name?.toString() : '',
                detail: shippingMethod.description != undefined ? shippingMethod.description?.toString() : '',
                amount:
                  shippingMethod.rates !== undefined && shippingMethod.rates[0].price?.centAmount !== undefined
                    ? CurrencyHelpers.formatMoney(shippingMethod.rates[0].price).toString()
                    : '0',
                identifier: shippingMethod.shippingMethodId,
              };
              newShippingMethods.push(newShippingMethod);
            }
          });
          const applePayShippingContactUpdate = {
            newTotal: newTotal,
            newLineItems: newLineItems,
            newShippingMethods: newShippingMethods,
          };
          resolve(applePayShippingContactUpdate);
        },

        onShippingMethodSelected: async (
          resolve: (arg0: {
            newTotal: ApplePayJS.ApplePayLineItem;
            newLineItems: ApplePayJS.ApplePayLineItem[];
          }) => void,
          reject: any,
          event: { shippingMethod: ApplePayJS.ApplePayShippingMethod },
        ) => {
          const res: Cart = await setShippingMethod(event.shippingMethod.identifier, '', new Date());
          const newTotal: ApplePayJS.ApplePayLineItem = applePayNewTotal(
            res,
            locale,
            formatCheckoutMessage({
              id: 'checkout.applepay.label',
              defaultMessage: 'American Golf (Trading) Limited',
            }),
          );

          const newLineItems: ApplePayJS.ApplePayLineItem[] = applepayLineItems(transaction, res, locale);

          const applePayShippingContactUpdate = {
            newTotal: newTotal,
            newLineItems: newLineItems,
          };
          resolve(applePayShippingContactUpdate);
        },

        onAuthorized: async (
          resolve: (arg0: ApplePayJS.ApplePayPaymentAuthorizationResult) => void,
          reject: any,
          event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
        ) => {
          if (data?.discountCodes?.length) {
            removeInvalidDiscountCodes(data.discountCodes);
          }
          const initialShippingAddressData = {
            streetName:
              event?.payment?.shippingContact?.addressLines !== undefined
                ? event?.payment?.shippingContact?.addressLines[0]
                : '',
            streetNumber:
              event?.payment?.shippingContact?.addressLines !== undefined
                ? event?.payment?.shippingContact?.addressLines[1]
                : '',
            firstName: event?.payment?.shippingContact?.givenName,
            lastName: event?.payment?.shippingContact?.familyName,
            email: event?.payment?.shippingContact?.emailAddress,
            phone: event?.payment?.shippingContact?.phoneNumber,
            postalCode: event?.payment?.shippingContact?.postalCode,
            city: event?.payment?.shippingContact?.locality,
            county: event?.payment?.shippingContact?.administrativeArea,
            country: event?.payment?.shippingContact?.countryCode,
          };
          const initialBillingAddressData = {
            streetName:
              event?.payment?.billingContact?.addressLines !== undefined
                ? event?.payment?.billingContact?.addressLines[0]
                : '',
            streetNumber:
              event?.payment?.billingContact?.addressLines !== undefined
                ? event?.payment?.billingContact?.addressLines[1]
                : '',
            firstName: event?.payment?.billingContact?.givenName,
            lastName: event?.payment?.billingContact?.familyName,
            email: event?.payment?.billingContact?.emailAddress,
            phone: event?.payment?.billingContact?.phoneNumber,
            postalCode: event?.payment?.billingContact?.postalCode,
            city: event?.payment?.billingContact?.locality,
            county: event?.payment?.billingContact?.administrativeArea,
            country: event?.payment?.billingContact?.countryCode,
          };

          const checkoutData = {
            account: {
              email:
                event?.payment?.shippingContact?.emailAddress !== undefined
                  ? event?.payment?.shippingContact?.emailAddress
                  : '',
            },
            shipping: initialShippingAddressData,
            billing: initialBillingAddressData,
          };

          const newTotal: ApplePayJS.ApplePayLineItem = applePayNewTotal(
            await cartRecalculate(),
            locale,
            formatCheckoutMessage({
              id: 'checkout.applepay.label',
              defaultMessage: 'American Golf (Trading) Limited',
            }),
          );
          const errors = validateAddress(initialShippingAddressData);

          if (errors.length) {
            const appleErr = errors.map(
              (error) => new ApplePayError('shippingContactInvalid', error.field, error.message),
            );

            const applePayShippingContactUpdate = {
              newTotal: newTotal,
              errors: appleErr, // Add all the generated errors to the array
            };

            reject(applePayShippingContactUpdate);
            return;
          }

          const res = await updateCart(checkoutData);
          const fluentstock = await getProductStock(productSKUs as string[], getCountry());

          const hasStock = res?.lineItems?.some((item) => {
            if (item.isPerpetualStock) {
              return true;
            }
            const availableQuantity = aggregatedQuantity(item.variant?.sku, fluentstock);
            return availableQuantity - (item?.count || 0) > 0;
          });

          const shippingMethods = await getAvailableShippingMethods();

          const isShippingMethodValid = validateShippingMethod(res, shippingMethods);

          if (!isShippingMethodValid || !hasStock) {
            const STATUS_FAILURE = 1;
            const applePayPaymentAuthorizationResult = {
              status: STATUS_FAILURE,
            };
            resolve(applePayPaymentAuthorizationResult);

            if (!hasStock) {
              router.push('/cart?errorId=error04');
            } else {
              router.push('/cart?errorId=error03');
            }
          } else {
            const orderNumber = Guid.newGuid(false, ['', 'xxxx-xxxx-yxxx']);

            const lineItems: Array<LineItem> = [];

            res.lineItems?.forEach((item) => {
              const lineItem: LineItem = {
                id: item.productId != undefined ? item.productId : '',
                quantity: item.count != undefined ? item.count.toString() : '0',
                description: item.name != undefined ? item.name : '',
                amountIncludingTax: item.price?.centAmount != undefined ? item.price.centAmount : 0,
              };
              lineItems.push(lineItem);
            });

            const makePaymentPayload: ApplePayPaymentRequestPayload = {
              amount: {
                currency: res?.sum?.currencyCode != undefined ? res?.sum?.currencyCode : 'GBP',
                value: res?.sum?.centAmount != undefined ? res?.sum?.centAmount : 0.0,
              },
              reference: orderNumber,
              paymentMethod: { type: 'applepay', applePayToken: applepayToken },
              returnUrl: `${window.location.origin}/en/thank-you?orderId={$orderNumber}&p2dl=true`,
              countryCode: 'GB',
              shopperLocale: locale,
              shopperReference: res.email != undefined ? res.email : '',
              lineItems: lineItems,
              metadata: { cartId: data?.cartId ? data.cartId : '' },
            };
            const cart = (await sdk.callAction({ actionName: 'cart/getCart' })) as any;

            const freezeCarts = await freezeCart(cart?.data?.cartId);

            let STATUS_SUCCESS = 0;

            const containSplitGivexPayments =
              data?.payments && data?.payments.some((payment) => payment.giftcardNumber);
            try {
              if (containSplitGivexPayments) {
              }
            } catch (error) {
              STATUS_SUCCESS = 1;
              console.error('Givex preauthorization error:', error);
            }
            const response = await sdk.callAction({
              actionName: 'adyen/makePayment',
              payload: makePaymentPayload,
            });
            const paymentResponse = (response.isError ? {} : response.data) as PaymentResponse;

            if (response.isError) {
              if (containSplitGivexPayments) {
                await cancelPreauthGiftcards();
              }
              toast.error(
                `${formatCheckoutMessage({
                  id: 'payment.failed',
                  defaultMessage: 'We could not process your payment, please try again later.',
                })}`,
              );
            }

            sessionStorage.setItem('pushToDataLayer', 'true');
            router.push(`/thank-you?orderId=${orderNumber}`);

            const applePayPaymentAuthorizationResult = {
              status: STATUS_SUCCESS,
            };
            resolve(applePayPaymentAuthorizationResult);
          }
        },

        onSubmit: async (state: any, component: any) => {
          applepayToken = state.data.paymentMethod.applePayToken;
        },
      };

      try {
        const applePayInstance = checkout.create('applepay', applePayConfig);
        applePayInstance && applePayInstance.mount('#applepay-container');
      } catch (e) {
        console.error('Apple Pay Express Error:', e);
      }
    };
    if (transaction && paymentMethods) {
      initAdyenCheckout();
    }
  }, [transaction, adyen, paymentMethods]);

  return (
    <div
      id="applepay-container"
      className={`apple-pay-button apple-pay-button-black mb-20 h-[44px] ${!isLoading ? '!block' : '!hidden'}`}
    ></div>
  );
};

export default ApplePayExpressButton;
